/* istanbul ignore file */
import { computed, watch } from 'vue';
import { useAvailability, useTraits } from '@common-vue/media-card-large';
import { useType } from '@modules/types';
import { useSize } from '@modules/size/composables';
import { useProductFlags } from '@modules/featureControl';
import { useUpc } from '@modules/upc';
import { useProduct } from '@modules/product';
import useTypeVariant from './useTypeVariant';
import useSizeVariant from './useSizeVariant';
import useColorVariant from './useColorVariant';

export default function useConsolidated3rdVariant() {
  const { productId } = useProduct();
  const { selectedColorId } = useTraits(productId);
  const { selectedSizeId } = useSize();
  const {
    size: sizeAvailability,
  } = useAvailability(productId);

  const { upcData } = useUpc();

  const { selectedTypeId } = useType();

  const { isTypeDrivenThirdVariantFlag: isTypeDriven } = useProductFlags();

  const isSizeDriven = !isTypeDriven;

  const { filteredTypes, availableTypes } = useTypeVariant();
  const { filteredSizeProps, availableSizes } = useSizeVariant();
  const {
    filteredColorProps, availableColors, priceToColors, priceToColorsExp,
  } = useColorVariant();

  /**
   * @type {{type?: TypeId | null, size?: SizeId | null, color: ColorId | null}[]}
   */
  const selectedTraits = [];

  if (isSizeDriven) {
    watch(selectedSizeId, (newSize, oldSize) => {
      selectedTraits[oldSize] = {
        type: selectedTypeId.value,
        color: selectedColorId.value,
      };

      selectedTraits[newSize] = selectedTraits[newSize] || {};
      const newTraits = selectedTraits[newSize];

      newTraits.color = newTraits?.color ?? availableColors.value[0]?.id ?? null;
      selectedColorId.value = newTraits.color;

      newTraits.type = newTraits?.type ?? availableTypes.value[0]?.id ?? null;
      selectedTypeId.value = newTraits.type;
    });
  }

  if (isTypeDriven) {
    watch(selectedTypeId, (newType, oldType) => {
      selectedTraits[oldType] = {
        size: selectedSizeId.value,
        color: selectedColorId.value,
      };

      selectedTraits[newType] = selectedTraits[newType] || {};
      const newTraits = selectedTraits[newType];

      newTraits.color = newTraits?.color ?? availableColors.value[0]?.id ?? null;
      selectedColorId.value = newTraits.color;

      newTraits.size = newTraits?.size ?? availableSizes.value[0]?.id ?? null;
      selectedSizeId.value = newTraits.size;
    });
  }

  const firstUpcKeyForColorAndDrivenTrait = computed(() => {
    let selectedUpcKey;
    if (isSizeDriven) {
      selectedUpcKey = Object.keys(upcData.value.upcMap || {}).find((upcKey) => {
        const [upcColor, upcSize] = upcKey.split('_');
        return upcColor === selectedColorId.value?.toString() && upcSize === selectedSizeId.value?.toString();
      });
    } else {
      selectedUpcKey = Object.keys(upcData.value.upcMap || {}).find((upcKey) => {
        const [upcColor, , upcType] = upcKey.split('_');
        return upcColor === selectedColorId.value?.toString() && upcType === selectedTypeId.value?.toString();
      });
    }
    return selectedUpcKey ? upcData.value.upcMap[selectedUpcKey] : null;
  });

  return {
    sizeProps: filteredSizeProps,
    selectedSizeId,
    types: filteredTypes,
    selectedTypeId,
    isTypeDriven,
    sizeAvailability,
    colorProps: filteredColorProps,
    firstUpcKeyForColorAndDrivenTrait,
    priceToColors,
    priceToColorsExp,
  };
}
